import React, { useContext, useEffect, useRef, useState } from 'react';
import { Form, Modal, Select, Switch } from 'antd';

import Auth0RolesContext from '@totem/components/administration/auth0/roles/Auth0RolesContext';
import {
  HierarchyReference,
  ObjectReference,
} from '@totem/components/administration/auth0/roles/types';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';

const EMPTY_ID = '000000000000000000000000';

const emptyRef = (): ObjectReference => ({ id: EMPTY_ID, name: '' });

const Auth0RoleEditDialog = () => {
  const { editingRole, modalOpen, onModalClose, onSave } =
    useContext(Auth0RolesContext);

  const [form] = Form.useForm();
  const orgRef = useRef<ObjectReference>(emptyRef());
  const regionRef = useRef<ObjectReference>(emptyRef());
  const buildingRef = useRef<ObjectReference>(emptyRef());
  const csRef = useRef<ObjectReference>(emptyRef());

  const [isTopLevel, setIsTopLevel] = useState(false);
  const [status, setStatus] = useState('Active');

  useEffect(() => {
    if (modalOpen && editingRole) {
      orgRef.current =
        editingRole.hierarchyReference?.organization ?? emptyRef();

      regionRef.current = editingRole.hierarchyReference?.region ?? emptyRef();

      buildingRef.current =
        editingRole.hierarchyReference?.building ?? emptyRef();

      csRef.current =
        editingRole.hierarchyReference?.controlSystem ?? emptyRef();

      setIsTopLevel(editingRole.isTopLevel ?? false);

      setStatus(editingRole.status ?? 'Active');
    } else if (!modalOpen) {
      orgRef.current = emptyRef();

      regionRef.current = emptyRef();

      buildingRef.current = emptyRef();

      csRef.current = emptyRef();

      setIsTopLevel(false);

      setStatus('Active');
    }
  }, [modalOpen, editingRole]);

  const handleHierarchyChange = (
    org: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    controlSystem: ObjectReference,
  ) => {
    orgRef.current = org;

    regionRef.current = region;

    buildingRef.current = building;

    csRef.current = controlSystem;
  };

  const handleOk = () => {
    if (!editingRole) return;

    const hierarchyReference: HierarchyReference = {
      organization: orgRef.current,
      region: regionRef.current,
      building: buildingRef.current,
      controlSystem: csRef.current,
    };

    onSave(editingRole.id, { hierarchyReference, isTopLevel, status });
  };

  const defaultBuildingId =
    editingRole?.hierarchyReference?.building?.id &&
    editingRole.hierarchyReference.building.id !== EMPTY_ID
      ? editingRole.hierarchyReference.building.id
      : undefined;

  return (
    <Modal
      title={`Edit Role: ${editingRole?.name ?? ''}`}
      open={modalOpen}
      onOk={handleOk}
      onCancel={onModalClose}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Organization / Region / Building / Control System">
          <OrgHierarchySelector
            showOrganization
            showRegions={false}
            showBuildings
            showControlSystems
            allowUnassignedOrganization
            allowUnassignedBuilding
            defaultBuildingId={defaultBuildingId}
            onChange={handleHierarchyChange}
          />
        </Form.Item>
        <Form.Item label="Status">
          <Select
            value={status}
            onChange={setStatus}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
        </Form.Item>
        <Form.Item label="Top Level">
          <Switch checked={isTopLevel} onChange={setIsTopLevel} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Auth0RoleEditDialog;
